import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import { EvolutionByDiscipline } from './EvolutionByDiscipline';
import { ZonesByMonth } from './ZonesByMonth';
import { PeriodCompare } from './PeriodCompare';
import { MonotonyTable } from '@/features/load/MonotonyTable';
import { PolarizationChart } from './PolarizationChart';
import { DisciplinePmcChart } from './DisciplinePmcChart';

/**
 * Página `/analiticas` — análisis profundo del entrenamiento (M2.13).
 *
 * 4 tabs internos. Mismo patrón que NutritionPage:
 * - `?tab=` controlado vía router.navigate.
 * - Solo renderizamos el panel activo (los hooks de cada panel disparan
 *   fetch al montar y guardan en cache, así que cambiar de tab y volver
 *   es instantáneo).
 */

const TAB_IDS = [
  'evolucion',
  'zonas',
  'periodos',
  'monotonia',
  'polarizacion',
] as const;
type TabId = (typeof TAB_IDS)[number];

const TAB_LABELS: Record<TabId, string> = {
  evolucion: 'Evolución',
  zonas: 'Zonas',
  periodos: 'Periodos',
  monotonia: 'Monotonía',
  polarizacion: 'Polarización & Balance',
};

function isTabId(v: unknown): v is TabId {
  return typeof v === 'string' && (TAB_IDS as readonly string[]).includes(v);
}

export function AnalyticsPage(): ReactElement {
  const router = useRouter();
  const search = router.state.location.search as Record<string, unknown> | undefined;
  const rawTab = search?.tab;
  const activeTab: TabId = isTabId(rawTab) ? rawTab : 'evolucion';

  const [localTab, setLocalTab] = useState<TabId>(activeTab);
  useEffect(() => {
    setLocalTab(activeTab);
  }, [activeTab]);

  const setTab = useCallback(
    (next: TabId) => {
      setLocalTab(next);
      router.navigate({
        to: '/analiticas',
        search: (prev: Record<string, unknown> | undefined) => ({
          ...(prev ?? {}),
          tab: next,
        }),
        replace: true,
      });
    },
    [router],
  );

  return (
    <section aria-labelledby="analytics-title" className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1
          id="analytics-title"
          className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
        >
          Análisis
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Evolución por disciplina, distribución de zonas, comparativa de periodos
          y monotonía semanal.
        </p>
      </header>

      <TabBar activeTab={localTab} onChange={setTab} />

      <div
        role="tabpanel"
        id="tabpanel-evolucion"
        aria-labelledby="tab-evolucion"
        hidden={localTab !== 'evolucion'}
      >
        {localTab === 'evolucion' && <EvolutionByDiscipline />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-zonas"
        aria-labelledby="tab-zonas"
        hidden={localTab !== 'zonas'}
      >
        {localTab === 'zonas' && <ZonesByMonth />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-periodos"
        aria-labelledby="tab-periodos"
        hidden={localTab !== 'periodos'}
      >
        {localTab === 'periodos' && <PeriodCompare />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-monotonia"
        aria-labelledby="tab-monotonia"
        hidden={localTab !== 'monotonia'}
      >
        {localTab === 'monotonia' && <MonotonyTable weeks={12} />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-polarizacion"
        aria-labelledby="tab-polarizacion"
        hidden={localTab !== 'polarizacion'}
      >
        {localTab === 'polarizacion' && (
          <div className="space-y-4">
            <PolarizationChart />
            <DisciplinePmcChart />
          </div>
        )}
      </div>
    </section>
  );
}

function TabBar({
  activeTab,
  onChange,
}: {
  activeTab: TabId;
  onChange: (next: TabId) => void;
}): ReactElement {
  return (
    <div
      role="tablist"
      aria-label="Secciones de Análisis"
      className="flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1 shadow-sm"
    >
      {TAB_IDS.map((id) => {
        const isActive = id === activeTab;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(id)}
            className={
              isActive
                ? 'rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2'
                : 'rounded-md px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2'
            }
          >
            {TAB_LABELS[id]}
          </button>
        );
      })}
    </div>
  );
}
